import { motion } from "framer-motion";
import { HeartHandshake, MessagesSquare, ShieldCheck, Sparkles } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { SectionHeading, Reveal, GradientButton } from "../components/primitives";

const CHAPTERS = [
  {
    n: "01",
    title: "Built for Canadian businesses",
    body: "Nova SecureNet is the website and digital services division of AMS Venture Holdings Inc., created to give small businesses across Canada reliable websites, practical technology solutions, and approachable digital security — all in one dependable place.",
  },
  {
    n: "02",
    title: "We remove the complexity",
    body: "Technology should work for you, not against you. We translate the technical into plain language and handle the details, so you can focus on running your business.",
  },
  {
    n: "03",
    title: "Partnerships, not transactions",
    body: "We're in it for the long run. From your first consultation to years of ongoing support, we show up, communicate clearly, and do quality work you can count on.",
  },
];

const VALUES = [
  { icon: HeartHandshake, title: "Long-term partnership", desc: "We invest in relationships, not one-off projects." },
  { icon: MessagesSquare, title: "Clear communication", desc: "No jargon, no surprises — just honest guidance." },
  { icon: Sparkles, title: "Quality work", desc: "Premium results, delivered with real craft." },
  { icon: ShieldCheck, title: "Trust & security", desc: "Your business, protected and set up correctly." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        eyebrow="About Nova SecureNet"
        title="Your dependable technology partner"
        sub="We make professional websites, digital security, and IT simple for small businesses — with a partnership that lasts."
      />

      {/* Manifesto chapters */}
      <section className="mx-auto max-w-6xl px-5 pb-8 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div className="space-y-6">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="flex gap-6 rounded-3xl border border-white/10 bg-white/[0.03] p-8">
                  <span className="font-heading text-4xl font-bold text-gradient">{c.n}</span>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-white">{c.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
              <img
                src="https://images.unsplash.com/photo-1610387853988-7a1623efb207?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt="The Nova SecureNet team"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/20 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8">
                <p className="text-xs uppercase tracking-wider text-brand-gold">The team</p>
                <p className="mt-1 font-heading text-lg font-semibold text-white">People who pick up the phone.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <SectionHeading align="center" eyebrow="What we stand for" title="The values behind every project" className="mb-14" />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <motion.div whileHover={{ y: -6 }} className="h-full rounded-3xl border border-white/10 bg-white/[0.03] p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-gradient">
                  <v.icon className="h-6 w-6 text-white" strokeWidth={2} />
                </span>
                <h3 className="mt-5 font-heading text-base font-semibold text-white">{v.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{v.desc}</p>
              </motion.div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 flex justify-center">
          <GradientButton to="/contact" data-testid="about-cta">Work With Us</GradientButton>
        </div>
      </section>
    </div>
  );
}
